import * as Sentry from '@sentry/vue';
import types from '../mutation-types';
import ConversationApi from '../../api/inbox/conversation';
import { debounce } from '@chatwoot/utils';

const state = {
  allWaitingCount: 0,
  myChatsCount: 0,
  myTeamChatsCount: 0,
  allChatsCount: 0,
};

export const getters = {
  getStats: $state => $state,
  getAllWaitingCount: $state => $state.allWaitingCount,
  getMyChatsCount: $state => $state.myChatsCount,
  getMyTeamChatsCount: $state => $state.myTeamChatsCount,
  getAllChatsCount: $state => $state.allChatsCount,
};

// Counts are shared across assignee tabs, pages, and sort orders.
const getViewKey = ({
  inboxId,
  status,
  labels,
  teamId,
  conversationType,
  queryData,
}) =>
  JSON.stringify([
    ConversationApi.url,
    queryData || {
      inboxId,
      status,
      labels,
      teamId,
      conversationType,
    },
  ]);

let activeView = { key: null, params: {}, committedRequestId: 0 };
let requestId = 0;

const isCurrentRequest = request =>
  request.view === activeView && request.id >= activeView.committedRequestId;

const commitCounts = (commit, meta, request) => {
  if (!isCurrentRequest(request)) return;

  activeView.committedRequestId = request.id;
  commit(types.SET_CONV_TAB_META, meta);
};

const fetchMetaData = async (commit, params, request) => {
  if (!isCurrentRequest(request)) return;

  try {
    const response = params.queryData
      ? await ConversationApi.filter({ queryData: params.queryData, page: 1 })
      : await ConversationApi.meta(params);
    const {
      data: { meta },
    } = response;
    commitCounts(commit, meta, request);
  } catch (error) {
    Sentry.captureException(error);
  }
};

const debouncedFetchMetaData = debounce(fetchMetaData, 1000, false, 5000);
const longDebouncedFetchMetaData = debounce(fetchMetaData, 7500, false, 20000);
const superLongDebouncedFetchMetaData = debounce(
  fetchMetaData,
  15000,
  false,
  30000
);

const metaDebouncers = {
  default: debouncedFetchMetaData,
  long: longDebouncedFetchMetaData,
  superLong: superLongDebouncedFetchMetaData,
};

const getTotalCount = $state =>
  $state.allWaitingCount +
  $state.myChatsCount +
  $state.myTeamChatsCount +
  $state.allChatsCount;

// Counts are 0 until a meta request succeeds; under load they stay 0, so treat
// the unknown case as a large account and poll slowest instead of fastest.
export const getMetaDebounceKey = totalCount => {
  if (totalCount > 2000 || totalCount === 0) return 'superLong';
  if (totalCount > 100) return 'long';
  return 'default';
};

export const actions = {
  // SeeGuest's sidebar counters are refreshed from outside the list (sidebar,
  // read receipts) with their own params, so no view-key gate here; request
  // ordering still drops responses that land after a newer one.
  get: ({ commit, state: $state }, params = activeView.params) => {
    requestId += 1;
    metaDebouncers[getMetaDebounceKey(getTotalCount($state))](commit, params, {
      view: activeView,
      id: requestId,
    });
  },
  onListRequestStarted(_, params) {
    const key = getViewKey(params);
    if (key !== activeView.key) {
      activeView = { key, params, committedRequestId: 0 };
    }
    requestId += 1;
    return { view: activeView, id: requestId };
  },
  set({ commit }, { meta, request }) {
    commitCounts(commit, meta, request);
  },
};

export const mutations = {
  [types.SET_CONV_TAB_META](
    $state,
    {
      all_waiting_count: allWaitingCount,
      my_chats_count: myChatsCount,
      my_team_chats_count: myTeamChatsCount,
      all_chats_count: allChatsCount,
    } = {}
  ) {
    $state.allWaitingCount = allWaitingCount;
    $state.myChatsCount = myChatsCount;
    $state.myTeamChatsCount = myTeamChatsCount;
    $state.allChatsCount = allChatsCount;
    $state.updatedOn = new Date();
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
